import EmptyProgramCourseList from '@/components/EmptyProgramCourseList';
import InputCheckboxWithTooltip from '@/components/InputCheckboxWithTooltip';
import LMSCourseCard from '@/components/LMSCourseCard';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { EntityType } from '@/models/EntityTypes';
import List from '@/models/List';
import { resetNavigationToSetScreen } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import {
    getProgramCoursesRequest,
    getProgramPrerequisitesRequest,
    getProgramRequest,
    removeCourseFromProgramRequest,
    reorderProgramCourseRequest,
    setProgramPrerequisitesRequest,
} from '@/store/program/action';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, DraggableProvided, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Switch from 'react-switch';
import './style.scss';

interface Props {
    preReqEnabled: boolean;
    setPreReqEnabled: Function;
}
const CourseList = ({ preReqEnabled, setPreReqEnabled }: Props) => {
    const { strings, colors, loading, courses, program, prerequisitesEnabled, isAdmin } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        colors: state.appOrganization.data.colors,
        loading: state.program.loading,
        courses: state.program.courses,
        program: state.program.programData,
        prerequisitesEnabled: state.program.prerequisitesEnabled,
        isAdmin: state.identity.data.user.admin,
    }));

    const dispatch = useDispatch();
    const { id: programId } = useParams<{ id: string }>();

    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [courseList, setCourseList] = useState<List[]>([]);

    useEffect(() => {
        resetNavigationToSetScreen(EntityType.PROGRAM, program.organizationId, isAdmin, useHistory);
    }, [program.organizationId, isAdmin]);

    const searchProgramCourse = () => dispatch(getProgramCoursesRequest(parseInt(programId)));

    useEffect(() => {
        if (!program || program.id !== parseInt(programId)) {
            dispatch(getProgramRequest(parseInt(programId)));
        }
        dispatch(getProgramPrerequisitesRequest(parseInt(programId)));
        searchProgramCourse();
    }, []);

    useEffect(() => {
        setPreReqEnabled(prerequisitesEnabled);
    }, [prerequisitesEnabled]);

    useEffect(() => {
        if (!loading) setInitialLoading(false);
    }, [loading]);

    useEffect(() => {
        setCourseList(courses);
    }, [courses]);

    const handleRequisitesChange = () => {
        const preReq = !preReqEnabled;
        setPreReqEnabled(preReq);
        dispatch(setProgramPrerequisitesRequest(parseInt(programId), preReq));
    };

    const handleRemoveCourse = (course: List) => {
        if (program.status === Constants.programStatus.DRAFT) {
            dispatch(removeCourseFromProgramRequest(parseInt(programId), course.id as number, preReqEnabled));
        }
    };

    const onDragEnd = (result: DropResult): void => {
        const { source, destination } = result;
        if (destination && source.droppableId === destination.droppableId) {
            const data = [...courseList];
            const [movedCourse]: List[] = data.splice(source.index, 1);
            dispatch(
                reorderProgramCourseRequest({
                    programId: parseInt(programId),
                    courseId: movedCourse.id || 0,
                    order: destination.index + 1,
                    prerequisites: preReqEnabled,
                }),
            );
            data.splice(destination.index, 0, movedCourse);
            setCourseList(data);
        }
    };

    return (
        <div className="program-course">
            <div className="program-course__header d-flex align-items-center">
                <Switch
                    checked={preReqEnabled}
                    checkedIcon={false}
                    className="align-middle"
                    disabled={false}
                    handleDiameter={8}
                    height={17}
                    offColor="#d1d1d1"
                    offHandleColor="#b3b3b3"
                    onChange={handleRequisitesChange}
                    onColor={colors[0]}
                    onHandleColor={colors[0]}
                    uncheckedIcon={false}
                    width={33}
                />
                <div className="d-flex flex-column ml-3">
                    <span className="align-middle ml-0">{strings.USE_PROGRAM_COURSE_SEQUENCE_AS_PREREQUISITE}</span>
                    <p className="m-0">
                        <strong>{strings.PREREQUISITE_RULE_EXCEPTION}</strong>
                    </p>
                </div>
            </div>
            <div className="program-course__body">
                {initialLoading ? (
                    <Loader />
                ) : courseList.length === 0 ? (
                    <EmptyProgramCourseList />
                ) : (
                    <div className="d-flex">
                        <div>
                            {courseList.map((course: List, index: number) => (
                                <div className="order" key={index}>
                                    <div className="order__counter">
                                        <span>{index + 1}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided: DroppableProvided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className="draggable-list">
                                        {courseList.map((course: List, index: number) => (
                                            <div className="course" key={course.id}>
                                                <Draggable
                                                    draggableId={(course.id && course.id.toString()) || course.bizId.toString()}
                                                    index={index}
                                                    isDragDisabled={courseList.length === 1}
                                                    key={course.id}
                                                >
                                                    {(providedDraggable: DraggableProvided) => (
                                                        <div
                                                            ref={providedDraggable.innerRef}
                                                            {...providedDraggable.draggableProps}
                                                            {...providedDraggable.dragHandleProps}
                                                            className="draggable-list-item"
                                                        >
                                                            <div className="course__draggable">
                                                                <i className="material-icons-outlined align-middle drag-indicator-icon">
                                                                    {Constants.icons.DRAG_INDICATOR}
                                                                </i>
                                                                <div className="course__draggable-card">
                                                                    <LMSCourseCard course={course} hideStatus mode="view" />
                                                                </div>
                                                                <div className="d-flex flex-column">
                                                                    <button
                                                                        className={`btn course__draggable-remove ${
                                                                            program.status === Constants.programStatus.DRAFT ? '' : 'disabled'
                                                                        }`}
                                                                        onClick={() => handleRemoveCourse(course)}
                                                                    >
                                                                        <span className="remove-btn">{strings.REMOVE}</span>
                                                                    </button>
                                                                    <InputCheckboxWithTooltip
                                                                        disabled
                                                                        initialValue
                                                                        label={strings.ITS_MANDATORY}
                                                                        onChange={() => {}}
                                                                        toolTip={strings.TOOLTIP_PROGRAM_COURSE_ITS_MANDATORY}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseList;
